// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — useTenantData Hook
// Central data fetching + computed analytical layers:
//   • buffer-adjusted financial gaps (subtracts legitimate internal consumption)
//   • shift staffing analyses (labor cost vs revenue correlation)
//   • unprofitable shifts converted to pseudo-gaps for Financial Radar display
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@lib/supabase';
import { applyBuffersToGaps } from '@services/bufferCalculator';
import { analyzeShifts, shiftsToGaps, aggregateLaborKpis } from '@services/shiftStaffingAnalyzer';

export function useTenantData(tenantId) {
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!tenantId) {
      setRawData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [
        tenantRes,
        branchesRes,
        employeesRes,
        productsRes,
        salesRes,
        ingredientsRes,
        invoicesRes,
        insightsRes,
        milestonesRes,
        gapsRes,
        featuresRes,
        marketRes,
        cancellationsRes,
        // NEW — enterprise analytical layer
        shiftStaffingRes,
        consumptionBuffersRes,
        aliasesRes
      ] = await Promise.all([
        supabase.from('tenants').select('*').eq('id', tenantId).maybeSingle(),
        supabase.from('branches').select('*').eq('tenant_id', tenantId).eq('is_active', true),
        supabase.from('pos_employees').select('*').eq('tenant_id', tenantId).order('honesty_score'),
        supabase.from('products').select('*').eq('tenant_id', tenantId).eq('is_active', true),
        supabase.from('sales_daily').select('*').eq('tenant_id', tenantId).order('sale_date', { ascending: false }).limit(90),
        supabase.from('ingredients').select('*').eq('tenant_id', tenantId),
        supabase.from('purchase_invoices').select('*').eq('tenant_id', tenantId).order('invoice_date', { ascending: false }).limit(50),
        supabase.from('insights').select('*').eq('tenant_id', tenantId).eq('is_dismissed', false).eq('is_resolved', false).order('created_at', { ascending: false }),
        supabase.from('growth_milestones').select('*').eq('tenant_id', tenantId).order('level').order('display_order'),
        supabase.from('financial_gaps').select('*').eq('tenant_id', tenantId).eq('is_resolved', false).order('detected_at', { ascending: false }),
        supabase.from('feature_flags').select('*').eq('tenant_id', tenantId),
        supabase.from('market_prices').select('*').order('material_name_en'),
        supabase.from('cancellations').select('*').eq('tenant_id', tenantId).eq('is_suspicious', true).order('cancelled_at', { ascending: false }).limit(20),
        // Enterprise tables — use .then to gracefully handle if tables don't exist yet
        supabase.from('shift_staffing').select('*').eq('tenant_id', tenantId).eq('is_active', true).then(
          r => r,
          () => ({ data: [] })
        ),
        supabase.from('consumption_buffers').select('*').eq('tenant_id', tenantId).eq('is_active', true).then(
          r => r,
          () => ({ data: [] })
        ),
        supabase.from('ingredient_aliases').select('*').eq('tenant_id', tenantId).then(
          r => r,
          () => ({ data: [] })
        )
      ]);

      const featureMap = {};
      (featuresRes.data || []).forEach(f => {
        featureMap[f.module_key] = f.is_enabled;
      });

      setRawData({
        tenant: tenantRes.data,
        branches: branchesRes.data || [],
        employees: employeesRes.data || [],
        products: productsRes.data || [],
        sales: salesRes.data || [],
        ingredients: ingredientsRes.data || [],
        invoices: invoicesRes.data || [],
        insights: insightsRes.data || [],
        milestones: milestonesRes.data || [],
        gaps_raw: gapsRes.data || [],
        features: featureMap,
        marketPrices: marketRes.data || [],
        cancellations: cancellationsRes.data || [],
        shiftStaffing: shiftStaffingRes.data || [],
        consumptionBuffers: consumptionBuffersRes.data || [],
        aliases: aliasesRes.data || []
      });
    } catch (err) {
      console.error('[useTenantData] Error:', err);
      setError(err.message || 'Failed to fetch tenant data');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ═══════════════════════════════════════════════════════════════════════
  // COMPUTED LAYER — applies buffers + labor analysis on top of raw data
  // ═══════════════════════════════════════════════════════════════════════
  const data = useMemo(() => {
    if (!rawData) return null;

    // 1. Buffer-adjust inventory gaps
    const bufferAdjustedGaps = applyBuffersToGaps({
      gaps: rawData.gaps_raw,
      buffers: rawData.consumptionBuffers,
      employees: rawData.employees,
      periodDays: 30
    });

    // 2. Analyze shift staffing
    const shiftAnalyses = analyzeShifts({
      shiftStaffing: rawData.shiftStaffing,
      sales: rawData.sales,
      branches: rawData.branches
    });

    // 3. Convert unprofitable shifts into gap-shaped records
    const laborGaps = shiftsToGaps(shiftAnalyses);

    // 4. Labor KPIs
    const laborKpis = aggregateLaborKpis(shiftAnalyses);

    // 5. Merged gaps = buffer-adjusted DB gaps + computed labor gaps
    const mergedGaps = [...bufferAdjustedGaps, ...laborGaps];

    return {
      ...rawData,
      gaps: mergedGaps,
      shiftAnalyses,
      laborKpis
    };
  }, [rawData]);

  return { data, loading, error, refresh: fetchData };
}
